import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AccountPageShell from "../components/account/AccountPageShell";

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [showResetCode, setShowResetCode] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleResetCodeChange = (event) => {
    setResetCode(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFieldErrors({});
    setSuccessMessage("");
    setIsLoading(true);

    try {
      if (!showResetCode) {
        // First step: Request reset code
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/forgot-password`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: email,
            }),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          setFieldErrors({ email: data.message });
          setIsLoading(false);
          return;
        }

        setSuccessMessage("Reset code sent to your email. Please check your inbox.");
        setShowResetCode(true);
        setIsLoading(false);
      } else {
        // Second step: Verify reset code
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/password-reset/verify`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: email,
              resetCode: resetCode,
            }),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          setFieldErrors({ resetCode: data.message });
          setIsLoading(false);
          return;
        }

        setSuccessMessage("Reset code verified successfully. Redirecting...");
        setTimeout(() => {
          navigate(`/reset-password?userId=${data.userId}&resetId=${data.resetId}&email=${email}`);
        }, 1500);
      }
    } catch (error) {
      console.error("Password reset request failed:", error);
      setFieldErrors({ form: error.message || "An error occurred. Please try again." });
      setIsLoading(false);
    }
  };

  return (
    <AccountPageShell>
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-7xl items-center justify-center px-5 py-10 sm:px-8 lg:px-10">
        <section className="w-full max-w-lg overflow-hidden rounded-xl border border-line bg-surface-850">
          <div className="border-b border-line px-5 py-5 sm:px-7">
            <p className="eyebrow-accent">ACCOUNT SECURITY</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">
              {showResetCode ? "Verify your email" : "Reset your password"}
            </h1>
            <p className="mt-2 text-sm leading-6 text-gray-400">
              {showResetCode
                ? "Enter the reset code we sent to your email address."
                : "We'll send you a secure code to confirm your identity."}
            </p>
          </div>

          <div className="p-5 sm:p-7">
            {successMessage && (
              <div role="status" aria-live="polite" className="mb-5 rounded-lg border border-emerald-900/50 bg-emerald-950/20 px-4 py-3 text-sm text-emerald-200">
                {successMessage}
              </div>
            )}
            {fieldErrors.form && (
              <div role="alert" className="mb-5 rounded-lg border border-red-900/60 bg-red-950/30 px-4 py-3 text-sm text-red-200">
                {fieldErrors.form}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="field-label">Email address</label>
                <input
                  type="email"
                  id="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="you@example.com"
                  disabled={showResetCode || isLoading}
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={fieldErrors.email ? "forgot-email-error" : undefined}
                  className="field disabled:cursor-not-allowed disabled:opacity-60"
                />
                {fieldErrors.email && <p id="forgot-email-error" role="alert" className="mt-1.5 text-sm text-accent-soft">{fieldErrors.email}</p>}
              </div>

              {showResetCode && (
                <div>
                  <label htmlFor="resetCode" className="field-label">Reset code</label>
                  <input
                    type="text"
                    id="resetCode"
                    autoComplete="one-time-code"
                    required
                    value={resetCode}
                    onChange={handleResetCodeChange}
                    placeholder="Enter the code from your email"
                    disabled={isLoading}
                    aria-invalid={Boolean(fieldErrors.resetCode)}
                    aria-describedby={fieldErrors.resetCode ? "forgot-code-error" : undefined}
                    className="field disabled:cursor-not-allowed disabled:opacity-60"
                  />
                  {fieldErrors.resetCode && <p id="forgot-code-error" role="alert" className="mt-1.5 text-sm text-accent-soft">{fieldErrors.resetCode}</p>}
                </div>
              )}

              <button type="submit" disabled={isLoading} className="btn-primary min-h-11 w-full gap-2">
                {isLoading && <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                {isLoading ? (showResetCode ? "Verifying code…" : "Sending code…") : (showResetCode ? "Verify code" : "Send reset code")}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-400">
              Remembered your password? <a href="/login" className="font-medium text-accent-soft hover:text-white">Sign in</a>
            </p>
          </div>
        </section>
      </div>
    </AccountPageShell>
  );
}

export default ForgotPassword;
